// Mirrors the backend roles (docs/superpowers/specs/2026-09-18-workspaces-and-database-connections-design.md).
// Used to show or hide actions only; the backend always checks again.
import { workspaceDatabases } from "./readingContext";

// bpExperts staff. Stored values from earlier releases read as these two.
export const STAFF_ROLES = [
  { key: "admin", label: "bpExperts admin", description: "Everything, in every workspace." },
  { key: "support", label: "Support", description: "Sees everything, changes nothing." },
];
// Earlier name, kept for screens not yet moved over.
export const PLATFORM_ROLES = STAFF_ROLES;

// Roles inside a workspace.
export const WORKSPACE_ROLES = [
  { key: "admin", label: "Admin", description: "Invites people, sets roles, manages views and sees every database of the workspace." },
  { key: "member", label: "Member", description: "Uses the databases the views they hold give them." },
];
export const ORG_ROLES = WORKSPACE_ROLES;

const STORED_STAFF_ADMIN = ["superAdmin", "admin", "contentManager"];
const STORED_WORKSPACE_ADMIN = ["owner", "admin"];

// Any stored platformRole -> "admin" | "support" | null.
export const staffRoleOf = (stored) => (STORED_STAFF_ADMIN.includes(stored) ? "admin" : stored === "support" ? "support" : null);

// Any stored workspace role -> "admin" | "member".
export const workspaceRoleOf = (stored) => (STORED_WORKSPACE_ADMIN.includes(stored) ? "admin" : "member");

// On a dedicated instance the admins are the customer's own: "Instance admin".
export const platformRoleLabel = (key, instanceMode = "shared") => {
  const role = staffRoleOf(key);
  if (role === "admin") return instanceMode === "dedicated" ? "Instance admin" : "bpExperts admin";
  if (role === "support") return "Support";
  return "Customer";
};

export const orgRoleLabel = (key) => (workspaceRoleOf(key) === "admin" ? "Admin" : "Member");

export const canPlatform = (user, permission) =>
  Array.isArray(user?.platformPermissions) && user.platformPermissions.includes(permission);

export const canInOrg = (user, permission, orgId) => {
  if (canPlatform(user, "tenants.manage")) return true;
  const workspace = (user?.workspaces || []).find((w) => w.id === orgId);
  return Boolean(workspace?.permissions?.includes(permission));
};

// Workspaces the user runs as Workspace admin.
export const manageableWorkspaces = (user) => (user?.workspaces || []).filter((w) => workspaceRoleOf(w.role) === "admin");

// Every workspace the user belongs to.
export const memberWorkspaces = (user) => user?.workspaces || [];

// Database names travel through URLs and older stored values, where the
// case may differ from the server's: compared without it everywhere.
const sameName = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// Whatever a screen would offer, kept inside the workspace the reader chose.
// One rule for every list of databases in the app, so the switcher, the
// console and the chip can't disagree about where the reader is.
const inChosenWorkspace = (user, names) => {
  const here = workspaceDatabases(user);
  return (names || []).filter((name) => here.some((one) => sameName(one, name)));
};

// "edit" on a database unlocks its content and configuration.
export const canEditDatabase = (user, database) =>
  canPlatform(user, "content.manage") || Boolean(database && user?.databaseModes?.[database] === "edit");

export const editableDatabases = (user) =>
  Object.keys(user?.databaseModes || {})
    .filter((database) => user.databaseModes[database] === "edit")
    .sort();

// The databases the content and configuration screens offer: staff see every
// database they can open; customers the ones they edit. Narrowed, like every
// other list of databases in the app, to the workspace the reader chose -
// a database of one of their other workspaces is reached by switching to it
// (utils/readingContext.js), never from inside this one.
export const contentDatabases = (user) =>
  inChosenWorkspace(user, canPlatform(user, "platform.view") ? user?.databases || [] : editableDatabases(user));

// Where those screens start: the database in use, else the main one, else the first.
export const startingDatabase = (user, databases) => {
  let current = null;
  try {
    current = localStorage.getItem("db");
  } catch (err) {
    current = null;
  }
  return [current, user?.mainDatabase].find((name) => name && databases.includes(name)) || databases[0] || "";
};

// Whether the user may open the console at all: staff, anyone who edits a
// database, or a Workspace admin of one (Display).
export const canOpenConsole = (user) =>
  canPlatform(user, "platform.view") ||
  editableDatabases(user).length > 0 ||
  (user?.databases || []).some((name) => (user?.adminDatabases || []).some((admin) => String(admin).toLowerCase() === String(name).toLowerCase()));

// Whether a library's name is shown to this person (product owner, 29 Sep
// 2026: "don't show database/library name to the public, only admin - so only
// show workspace and view name to the public").
//
// A library's name is ours, not the reader's: it is a key in our registry
// ("bpexpertsinternal", "BFKG2.0"), and a customer has no use for it. What
// says where they are is the workspace they are in and the view they read it
// through. So the bar, the account menu and Home name those two, and the
// library only for the people who already meet database names elsewhere -
// which is exactly the people the console opens for. Anyone else would be
// told one name in the bar and another in a screen they can reach.
//
// **It is not a secret.** A library's name is in the address of every deep
// link (`/{database}/map/{guid}`) and in the header of every request, so this
// is about what a reader is shown, never about concealment: nothing here is
// a security boundary, and what a person may open is decided on the server
// by the views they hold.
// A Workspace admin is asked for twice because the profile says it twice:
// `adminDatabases` (the databases their admin role covers) and the `role` on
// each entry of `workspaces`. Either one is enough, and asking for both means
// a profile that carries only the second - an older server, or a workspace
// whose databases hadn't resolved - doesn't quietly turn an admin into a
// client and take the library's name off their bar.
export const showsLibraryName = (user) => canOpenConsole(user) || manageableWorkspaces(user).length > 0;

// A signed-in customer with no database to open (their groups grant none, or
// they left every workspace): the app shows a card saying whom to ask
// instead of pages that fail. Staff always have somewhere to go.
//
// One test for both ways the app finds out: the profile it loaded, and a
// request the server refused with NO_DATABASE_MSG (serverSaysSo).
export const hasNoDatabase = (user, { serverSaysSo = false } = {}) => {
  if (!user || staffRoleOf(user.platformRole)) return false;
  return serverSaysSo || (Array.isArray(user.databases) && user.databases.length === 0);
};

// The server's answer when a request names no database it may use.
export const NO_DATABASE_MSG = "No database assigned";

// Members of a trial-plan workspace don't leave it: trial access ends on its own.
export const inTrialWorkspace = (user) => {
  const id = user?.workspace?.id;
  if (!id) return false;
  return (user?.workspaces || []).some((w) => w.id === id && w.plan === "trial");
};

// Who configures how a database is shown (Console -> Display): staff with
// content.manage, or a Workspace admin of the workspace that owns it. Edit
// mode from a group edits content, not how the database looks.
export const administersDatabase = (user, database) =>
  canPlatform(user, "content.manage") || Boolean(database && (user?.adminDatabases || []).some((name) => sameName(name, database)));

// The databases Console -> Display offers: staff see every database they can
// open; customers the ones their workspace admin role covers.
export const viewDatabases = (user) =>
  inChosenWorkspace(
    user,
    canPlatform(user, "platform.view")
      ? user?.databases || []
      : (user?.databases || []).filter((name) => (user?.adminDatabases || []).some((admin) => sameName(admin, name)))
  );
